import { Upload } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useEquipStationExport } from "@/lib/use-equip-station-export";
import {
  ProposedStationsTable,
  type ProposedStationRow,
} from "./ProposedStationsTable";

interface CityReviewTabProps {
  collectionId: string;
}

export function CityReviewTab({ collectionId }: CityReviewTabProps) {
  const { data: summary, isLoading: loadingSummary } =
    trpc.physicalMapping.getCityReviewSummary.useQuery(
      { collectionId },
      { enabled: !!collectionId },
    );

  const { data: stations, isLoading: loadingStations } =
    trpc.physicalMapping.listApprovedStations.useQuery(
      { collectionId },
      { enabled: !!collectionId },
    );

  const counts = summary?.counts;
  const mainDenominator = summary?.hub_size ?? counts?.main_total ?? 0;
  const { exportToEquip, isExporting } = useEquipStationExport(collectionId);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div>
          <h2 className="text-sm font-semibold">City Review</h2>
          <p className="text-xs text-muted-foreground">
            Track city validation of approved stations.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void exportToEquip()}
          disabled={isExporting}
        >
          <Upload className="h-4 w-4 mr-1.5" />
          Export to Equip dashboard
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 p-4 border-b">
          {loadingSummary || !counts ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[78px] w-full rounded-lg" />
            ))
          ) : (
            <>
              <StatCard
                label="Main reviewed"
                value={`${counts.main_reviewed}/${mainDenominator}`}
                hint={
                  summary?.hub_size != null
                    ? `of ${summary.hub_size} hub size`
                    : `of ${counts.main_total} main`
                }
              />
              <StatCard
                label="Approvals"
                value={counts.approve}
                tone="emerald"
              />
              <StatCard label="Rejections" value={counts.reject} tone="rose" />
              <StatCard
                label="Needs change"
                value={counts.needs_change}
                tone="amber"
              />
              <StatCard label="Backup" value={counts.backup_total} />
            </>
          )}
        </div>

        {/* Proposed stations table */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Proposed stations
              </h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Tag each station as Main (part of the hub) or Backup. Click a
                row for full details.
              </p>
            </div>
          </div>
          <ProposedStationsTable
            collectionId={collectionId}
            rows={(stations ?? []) as ProposedStationRow[]}
            isLoading={loadingStations}
          />
        </div>
      </div>

    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone = "muted",
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: "muted" | "emerald" | "rose" | "amber";
}) {
  const toneClass =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "rose"
        ? "text-rose-700"
        : tone === "amber"
          ? "text-amber-700"
          : "text-foreground";
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className={cn("text-2xl font-semibold mt-1", toneClass)}>
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
      )}
    </div>
  );
}
